import { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../context/ThemeContext';
import { supabase } from '../lib/supabase';
import MyStoreLogin from './mystore/MyStoreLogin';
import MyStoreNotRegistered from './mystore/MyStoreNotRegistered';
import MyStoreIntake from './mystore/MyStoreIntake';
import MyStoreWaiting from './mystore/MyStoreWaiting';
import MyStoreContract from './mystore/MyStoreContract';
import MyStoreDashboard from './mystore/MyStoreDashboard';

const MyStorePortal = () => {
  const { theme } = useTheme();
  const [session, setSession] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authReady, setAuthReady] = useState(false);

  // Listen for auth state changes
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setAuthReady(true);
    });

    // Check initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setAuthReady(true);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Fetch user data from users table
  const fetchUserData = useCallback(async () => {
    if (!session?.user) {
      setUserData(null);
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('users')
        .select('id, is_location, intake_completed, approved_by_admin, contract_agreed, contract_agreed_at, location_data')
        .eq('auth_id', session.user.id)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching user data:', error);
      }

      setUserData(data || null);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  }, [session?.user]);

  useEffect(() => {
    if (authReady) {
      fetchUserData();
    }
  }, [authReady, fetchUserData]);

  const refreshUserData = () => {
    setLoading(true);
    fetchUserData();
  };

  const handleSignOut = async () => {
    setSession(null);
    setUserData(null);
    await supabase.auth.signOut({ scope: 'local' });
  };

  // Loading state
  if (!authReady || loading) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: theme.bg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <div style={{
          width: 32,
          height: 32,
          border: `3px solid ${theme.border}`,
          borderTopColor: theme.accent,
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite',
        }} />
      </div>
    );
  }

  // No session -> Login
  if (!session) {
    return <MyStoreLogin onAuthSuccess={refreshUserData} />;
  }

  // No user row or not registered as location
  if (!userData || !userData.is_location) {
    return <MyStoreNotRegistered refreshUserData={refreshUserData} onSignOut={handleSignOut} />;
  }

  // Intake not completed
  if (!userData.intake_completed) {
    return <MyStoreIntake refreshUserData={refreshUserData} onSignOut={handleSignOut} />;
  }

  // Not approved yet
  if (!userData.approved_by_admin) {
    return <MyStoreWaiting refreshUserData={refreshUserData} onSignOut={handleSignOut} userData={userData} />;
  }

  // Contract not signed
  if (!userData.contract_agreed) {
    return <MyStoreContract refreshUserData={refreshUserData} onSignOut={handleSignOut} />;
  }

  // All flags true -> Dashboard
  return <MyStoreDashboard onSignOut={handleSignOut} userData={userData} />;
};

export default MyStorePortal;
